import { Field } from '#app/components/form/field.tsx'
import { YesNoField } from '#app/components/form/yes-no-field.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { RemoveButton } from '#app/routes/mocra+/_form+/components/remove-button.tsx'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type FieldErrors = readonly string[]

export type Props = {
	readonly resources: SectionResources
	readonly documentLabel: string
	readonly description: string
	readonly categoryBadgeLabel: string
	readonly categoryBadgeClassName: string
	readonly isRequired: boolean
	readonly isCompleteName: string
	readonly hasDraftName: string
	readonly needsHelpName: string
	readonly locationName: string
	readonly isCompleteValue: 'true' | 'false' | undefined
	readonly hasDraftValue: 'true' | 'false' | undefined
	readonly needsHelpValue: 'true' | 'false' | undefined
	readonly locationValue: string
	readonly categoryLabelLower: string
	readonly showDraftQuestion: boolean
	readonly showHelpQuestion: boolean
	readonly showLocationField: boolean
	readonly isCompleteErrors: FieldErrors
	readonly hasDraftErrors: FieldErrors
	readonly needsHelpErrors: FieldErrors
	readonly locationErrors: FieldErrors
	readonly onCompleteChange: (value: 'true' | 'false') => void
	readonly onHasDraftChange: (value: 'true' | 'false') => void
	readonly onNeedsHelpChange: (value: 'true' | 'false') => void
	readonly onLocationChange: (value: string) => void
	readonly onRemove?: () => void
	readonly removeAriaLabel?: string
}

export function PolicyDocumentItem({
	resources,
	documentLabel,
	description,
	categoryBadgeLabel,
	categoryBadgeClassName,
	isRequired,
	isCompleteName,
	hasDraftName,
	needsHelpName,
	locationName,
	isCompleteValue,
	hasDraftValue,
	needsHelpValue,
	locationValue,
	categoryLabelLower,
	showDraftQuestion,
	showHelpQuestion,
	showLocationField,
	isCompleteErrors,
	hasDraftErrors,
	needsHelpErrors,
	locationErrors,
	onCompleteChange,
	onHasDraftChange,
	onNeedsHelpChange,
	onLocationChange,
	onRemove,
	removeAriaLabel,
}: Props) {
	const locationLabel =
		isCompleteValue === 'true'
			? 'Where is the approved document hosted?'
			: 'Where can we review the latest draft?'
	const locationDescription =
		isCompleteValue === 'true'
			? 'Share a SharePoint link, drive path, or system location.'
			: 'Link us to the working draft so reviewers can follow up.'

	return (
		<Section
			title={documentLabel}
			description={description}
			meta={
				<div className="flex items-center gap-2">
					<Badge variant="outline" className={categoryBadgeClassName}>
						{categoryBadgeLabel}
					</Badge>
					{isRequired ? (
						<Badge
							variant="outline"
							className="border-transparent bg-rose-100 text-rose-800 hover:bg-rose-200"
						>
							Required
						</Badge>
					) : (
						<Badge
							variant="outline"
							className="border-transparent bg-slate-100 text-slate-800 hover:bg-slate-200"
						>
							Optional
						</Badge>
					)}
				</div>
			}
			childrenClassName="space-y-4"
			mocraSections={resources.mocraSections}
			templates={resources.templates}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex-1 space-y-4">
					<YesNoField
						labelProps={{
							children: `Is this ${categoryLabelLower} completed and in active use?`,
						}}
						yesProps={{
							name: isCompleteName,
							value: 'true',
							checked: isCompleteValue === 'true',
							onChange: () => onCompleteChange('true'),
						}}
						noProps={{
							name: isCompleteName,
							value: 'false',
							checked: isCompleteValue === 'false',
							onChange: () => onCompleteChange('false'),
						}}
						errors={
							isCompleteErrors.length > 0 ? [...isCompleteErrors] : undefined
						}
					/>
					{showDraftQuestion ? (
						<YesNoField
							labelProps={{
								children: `Do you already have a draft for the ${categoryLabelLower}?`,
							}}
							yesProps={{
								name: hasDraftName,
								value: 'true',
								checked: hasDraftValue === 'true',
								onChange: () => onHasDraftChange('true'),
							}}
							noProps={{
								name: hasDraftName,
								value: 'false',
								checked: hasDraftValue === 'false',
								onChange: () => onHasDraftChange('false'),
							}}
							errors={
								hasDraftErrors.length > 0 ? [...hasDraftErrors] : undefined
							}
						/>
					) : null}
					{showHelpQuestion ? (
						<YesNoField
							labelProps={{
								children: 'Do you need help or a template to get this drafted?',
							}}
							yesProps={{
								name: needsHelpName,
								value: 'true',
								checked: needsHelpValue === 'true',
								onChange: () => onNeedsHelpChange('true'),
							}}
							noProps={{
								name: needsHelpName,
								value: 'false',
								checked: needsHelpValue === 'false',
								onChange: () => onNeedsHelpChange('false'),
							}}
							errors={
								needsHelpErrors.length > 0 ? [...needsHelpErrors] : undefined
							}
						/>
					) : null}
					{showLocationField ? (
						<Field
							labelProps={{ children: locationLabel }}
							inputProps={{
								name: locationName,
								type: 'text',
								value: locationValue,
								onChange: (event) => onLocationChange(event.target.value),
							}}
							descriptionProps={{ children: locationDescription }}
							errors={
								locationErrors.length > 0 ? [...locationErrors] : undefined
							}
						/>
					) : null}
				</div>
				{isRequired || !onRemove ? null : (
					<RemoveButton
						onClick={onRemove}
						ariaLabel={removeAriaLabel}
						className="sm:ml-4 sm:self-start"
					/>
				)}
			</div>
		</Section>
	)
}
